export interface MapTileArchive {
  iso: string
  key: string
  url: string
}

export interface MapTileManifest {
  schema_version: number
  countries: Record<string, MapTileArchive>
  aliases: Record<string, string>
}

export interface ResolvedMapTiles {
  archiveUrl: string
  country: string
}

const manifestRequests = new Map<string, Promise<MapTileManifest>>()

export const normalizeCountry = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null
  }

  const normalized = value
    .trim()
    .toLowerCase()
    .replace(/[._-]+/g, ' ')
    .replace(/\s+/g, ' ')

  return normalized || null
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const normalizedBaseUrl = (baseUrl: string): URL => {
  const parsed = new URL(baseUrl)
  const isLocalHttp =
    parsed.protocol === 'http:' &&
    ['localhost', '127.0.0.1', '[::1]'].includes(parsed.hostname)

  if (parsed.protocol !== 'https:' && !isLocalHttp) {
    throw new Error('Map tile base URL must use HTTPS')
  }

  parsed.pathname = `${parsed.pathname.replace(/\/+$/, '')}/`
  parsed.search = ''
  parsed.hash = ''
  return parsed
}

export const parseMapTileManifest = (
  value: unknown,
  baseUrl: string
): MapTileManifest => {
  if (!isRecord(value) || value.schema_version !== 1) {
    throw new Error('Unsupported map tile manifest')
  }

  if (!isRecord(value.countries) || !isRecord(value.aliases)) {
    throw new Error('Invalid map tile manifest')
  }

  const base = normalizedBaseUrl(baseUrl)
  const countries: Record<string, MapTileArchive> = {}
  const aliases: Record<string, string> = {}

  for (const [isoValue, archiveValue] of Object.entries(
    value.countries
  )) {
    const iso = normalizeCountry(isoValue)

    if (
      !iso ||
      !/^[a-z]{2}$/.test(iso) ||
      !isRecord(archiveValue) ||
      archiveValue.iso !== iso ||
      typeof archiveValue.key !== 'string' ||
      typeof archiveValue.url !== 'string' ||
      !new RegExp(
        `^archives/${iso}/[A-Za-z0-9._-]+-[a-f0-9]{64}\\.pmtiles$`
      ).test(archiveValue.key)
    ) {
      throw new Error('Invalid map tile archive entry')
    }

    const expectedUrl = new URL(archiveValue.key, base).toString()
    const archiveUrl = new URL(archiveValue.url)

    if (
      archiveUrl.toString() !== expectedUrl ||
      archiveUrl.origin !== base.origin
    ) {
      throw new Error('Unsafe map tile archive URL')
    }

    countries[iso] = {
      iso,
      key: archiveValue.key,
      url: archiveUrl.toString(),
    }
  }

  for (const [aliasValue, isoValue] of Object.entries(
    value.aliases
  )) {
    const alias = normalizeCountry(aliasValue)
    const iso = normalizeCountry(isoValue)

    if (!alias || !iso || !countries[iso]) {
      throw new Error('Invalid map tile country alias')
    }

    aliases[alias] = iso
  }

  return {
    schema_version: 1,
    countries,
    aliases,
  }
}

export const resolveMapTiles = (
  manifest: MapTileManifest,
  countryValue: unknown
): ResolvedMapTiles => {
  const normalized = normalizeCountry(countryValue)

  if (!normalized) {
    throw new Error('Delivery country is unavailable')
  }

  const country = manifest.countries[normalized]
    ? normalized
    : manifest.aliases[normalized]
  const archive = country ? manifest.countries[country] : undefined

  if (!country || !archive) {
    throw new Error(`Maps are unavailable for "${normalized}"`)
  }

  return {
    archiveUrl: archive.url,
    country,
  }
}

export const fetchMapTileManifest = (
  baseUrl: string
): Promise<MapTileManifest> => {
  const base = normalizedBaseUrl(baseUrl)
  const cacheKey = base.toString()
  const cached = manifestRequests.get(cacheKey)

  if (cached) {
    return cached
  }

  const request = fetch(new URL('manifest.json', base).toString())
    .then(async response => {
      if (!response.ok) {
        throw new Error(
          `Map tile manifest returned HTTP ${response.status}`
        )
      }

      return parseMapTileManifest(await response.json(), cacheKey)
    })
    .catch(error => {
      manifestRequests.delete(cacheKey)
      throw error
    })

  manifestRequests.set(cacheKey, request)
  return request
}
